/**
 * OriginationExtensions — Vague 14 (mode strictement additif).
 *
 * Composants exposés :
 *   <OriginationHeaderStrip />       Banner position + toggle actifs + glossaire (placé en haut)
 *   <OriginationFractaleAscendante /> Onglet "Fractale ↑" (5 niveaux Micro→Macro)
 *   <OppActionButtons opp={...} />   3 boutons (Friction Gap, Chaînage, Utility Mapping) par opp
 *   <FrictionGapModal />             Modal résultat NOAM
 *   <GlossaryDrawer />               Drawer avec définitions issues de la doctrine active
 */
import { useEffect, useState, createContext, useContext } from "react";
import { Link } from "react-router-dom";
import {
  Layers, BookOpen, AlertTriangle, ArrowUpRight, Network, GitBranch,
  X as XIcon, Sparkles, MapPin, ChevronUp, ChevronDown, Loader2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import Carto360Button from "@/components/Carto360Button";
import { startLLMJob } from "@/components/LLMJobsTray";

// ============================================================================
// Context — toggle Avec/Sans actifs partagé entre les deux onglets
// ============================================================================
const ActifsContext = createContext({ withActifs: true, setWithActifs: () => {} });
export const useActifs = () => useContext(ActifsContext);

export function ActifsProvider({ children }) {
  const [withActifs, setWithActifs] = useState(() => {
    try { return localStorage.getItem("noam.orig.withActifs") !== "0"; }
    catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("noam.orig.withActifs", withActifs ? "1" : "0"); }
    catch { /* defensive */ }
  }, [withActifs]);
  return (
    <ActifsContext.Provider value={{ withActifs, setWithActifs }}>
      {children}
    </ActifsContext.Provider>
  );
}

// ============================================================================
// Header strip — Position auto + toggle + glossaire
// ============================================================================
export function OriginationHeaderStrip() {
  const { t } = useTranslation();
  const { withActifs, setWithActifs } = useActifs();
  const [position, setPosition] = useState(null);
  const [glossaryOpen, setGlossaryOpen] = useState(false);

  useEffect(() => {
    api.get("/workspace/origination/positionnement-auto")
      .then((r) => setPosition(r.data))
      .catch(() => setPosition(null));
  }, []);

  return (
    <>
      <div
        className="flex items-center gap-3 flex-wrap p-3 rounded-lg bg-gradient-to-r from-[#7F77DD]/10 to-transparent border border-[#7F77DD]/20"
        data-testid="orig-header-strip"
      >
        {position && (
          <div className="flex items-center gap-2 flex-1 min-w-0" data-testid="orig-position-banner">
            <MapPin className="w-3.5 h-3.5 text-[#7F77DD] flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                {t("saas.origination.youAreHere", { defaultValue: "Vous êtes ici" })}
              </div>
              <div className="text-xs text-zinc-100 truncate">
                {position.label_fr}
                {position.actifs?.total > 0 && (
                  <span className="text-zinc-500"> · {position.actifs.total} actif{position.actifs.total > 1 ? "s" : ""}</span>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => setWithActifs(!withActifs)}
            className={`text-[10px] px-2.5 py-1 rounded border transition ${
              withActifs
                ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                : "bg-zinc-500/10 text-zinc-400 border-zinc-500/30"
            }`}
            data-testid="orig-toggle-actifs"
            title={t("saas.origination.toggleActifsHelp", { defaultValue: "Bascule les analyses (Friction Gap, scénarios) avec ou sans la prise en compte de vos actifs immobiliers/courtage." })}
          >
            {withActifs
              ? t("saas.origination.withActifs", { defaultValue: "Avec mes actifs" })
              : t("saas.origination.withoutActifs", { defaultValue: "Sans mes actifs" })}
          </button>
          <button
            onClick={() => setGlossaryOpen(true)}
            className="text-[10px] px-2.5 py-1 rounded border border-white/15 hover:border-white/30 text-zinc-300 inline-flex items-center gap-1.5"
            data-testid="orig-open-glossary"
          >
            <BookOpen className="w-3 h-3" />
            {t("saas.origination.glossary", { defaultValue: "Glossaire" })}
          </button>
        </div>
      </div>
      <GlossaryDrawer open={glossaryOpen} onClose={() => setGlossaryOpen(false)} />
    </>
  );
}

// ============================================================================
// Glossary Drawer
// ============================================================================
function GlossaryDrawer({ open, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/workspace/origination/glossaire")
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose} data-testid="orig-glossary-drawer">
      <div className="absolute inset-0 bg-black/60" />
      <div
        className="relative w-full max-w-md h-full bg-[var(--noam-sidebar)] border-l border-white/10 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[var(--noam-sidebar)] border-b border-white/10 p-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-[#7F77DD]" />
            <h3 className="text-sm font-semibold text-zinc-100">Glossaire — Doctrine active</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded" data-testid="orig-glossary-close">
            <XIcon className="w-4 h-4 text-zinc-400" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          {loading && <Loader2 className="w-4 h-4 animate-spin text-zinc-500" />}
          {!loading && items.length === 0 && (
            <div className="text-xs text-zinc-500">Aucun terme disponible.</div>
          )}
          {!loading && items.map((it) => (
            <div key={it.term} className="border border-white/5 rounded p-3 bg-white/[0.02]" data-testid={`glossary-term-${encodeURIComponent(it.term)}`}>
              <div className="text-sm font-semibold text-zinc-100">{it.term}</div>
              <div className="text-xs text-zinc-400 mt-1">{it.definition}</div>
              <div className="text-[10px] text-zinc-600 mt-2">Source : {it.source}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Fractale ascendante — onglet · V23.15.41 redesign complet
// ============================================================================
// Vue verticale storytelling Micro → Cosmique.
// Chaque niveau : couleur signature, narrative doctrinale, buckets, lignes de force.
// Les doctrines restent le moteur ; la vue en projette la lecture visuelle.
const LEVEL_STYLES = {
  1: { color: "#EFD127", label: "Micro",       hue: "230 209 39", icon: "◆" },
  2: { color: "#EF9F27", label: "Meso",        hue: "239 159 39", icon: "◇" },
  3: { color: "#E24B4A", label: "Macro",       hue: "226 75 74",  icon: "◈" },
  4: { color: "#A855F7", label: "Systémique",  hue: "168 85 247", icon: "◉" },
  5: { color: "#7F77DD", label: "Cosmique",    hue: "127 119 221", icon: "✦" },
};

export function OriginationFractaleAscendante() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedLevel, setExpandedLevel] = useState(1);
  const [factor, setFactor] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get("/workspace/origination/fractale-ascendante")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    api.get("/workspace/valuations/global-friction-factor")
      .then((r) => setFactor(r.data?.factor ?? 1.0))
      .catch(() => setFactor(1.0));
  }, []);

  if (loading) {
    return (
      <div className="text-zinc-500 text-sm py-10 text-center" data-testid="orig-fractale-asc-loading">
        <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Consolidation de la fractale ascendante…
      </div>
    );
  }
  if (!data) return <div className="text-zinc-500 text-sm">Données indisponibles.</div>;

  const levels = data.levels || [];
  const totalOpps = data.total_opportunites || 0;

  return (
    <div className="space-y-5" data-testid="orig-fractale-asc">
      {/* Bandeau doctrinal + rappel */}
      <div className="noam-card p-4 sm:p-5 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            background: "linear-gradient(180deg, #EFD127 0%, #EF9F27 25%, #E24B4A 50%, #A855F7 75%, #7F77DD 100%)",
          }}
        />
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-[#7F77DD]">
              Fractale ascendante · projection doctrinale
            </div>
            <div className="noam-display text-xl font-semibold mt-1 flex items-baseline gap-2 flex-wrap">
              <span className="text-[#EFD127]">Micro</span>
              <span className="text-zinc-600 text-sm">↑</span>
              <span className="text-[#EF9F27]">Meso</span>
              <span className="text-zinc-600 text-sm">↑</span>
              <span className="text-[#E24B4A]">Macro</span>
              <span className="text-zinc-600 text-sm">↑</span>
              <span className="text-[#A855F7]">Systémique</span>
              <span className="text-zinc-600 text-sm">↑</span>
              <span className="text-[#7F77DD]">Cosmique</span>
            </div>
            <p className="text-xs text-zinc-400 mt-2 leading-relaxed max-w-3xl">
              {data.doctrine_reminder || "Replace chaque opportunité dans son courant systémique et civilisationnel. Clique sur un niveau pour l'explorer."}
            </p>
          </div>
          <div className="text-right">
            <div className="text-[10px] text-zinc-500 uppercase tracking-widest">Pipeline</div>
            <div className="text-2xl font-semibold text-white tabular-nums">{totalOpps}</div>
            <div className="text-[10px] text-zinc-500">opportunités totales</div>
          </div>
        </div>
      </div>

      {/* Vertical timeline — chaque niveau comme une strate */}
      <div className="relative" data-testid="orig-fractale-timeline">
        {/* Ligne de flux verticale (SVG) */}
        <svg
          className="absolute left-6 top-4 bottom-4 w-0.5 pointer-events-none"
          style={{ height: "calc(100% - 32px)" }}
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="fractale-flow" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#EFD127" stopOpacity="0.8" />
              <stop offset="25%" stopColor="#EF9F27" stopOpacity="0.8" />
              <stop offset="50%" stopColor="#E24B4A" stopOpacity="0.8" />
              <stop offset="75%" stopColor="#A855F7" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#7F77DD" stopOpacity="0.8" />
            </linearGradient>
          </defs>
          <line x1="1" y1="0" x2="1" y2="100%" stroke="url(#fractale-flow)" strokeWidth="2" />
        </svg>

        <div className="space-y-3">
          {levels.map((l) => {
            const st = LEVEL_STYLES[l.n] || LEVEL_STYLES[3];
            const expanded = expandedLevel === l.n;
            return (
              <FractaleLevelCard
                key={l.n}
                level={l}
                style={st}
                expanded={expanded}
                factor={factor}
                onToggle={() => setExpandedLevel(expanded ? null : l.n)}
                t={t}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

function FractaleLevelCard({ level, style, expanded, factor, onToggle, t }) {
  const buckets = level.buckets || [];
  const hasBuckets = buckets.length > 0;
  const totalOpps = buckets.reduce((s, b) => s + (b.count || 0), 0);
  const totalTicket = buckets.reduce((s, b) => s + (b.ticket_total_meur || 0), 0);

  return (
    <div className="relative pl-12" data-testid={`orig-fractale-level-${level.n}`}>
      {/* Node bullet on the flow line */}
      <div
        className="absolute left-4 top-4 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold z-10 transition-all"
        style={{
          background: style.color,
          boxShadow: expanded ? `0 0 0 4px rgba(${style.hue}, 0.25), 0 0 20px rgba(${style.hue}, 0.5)` : `0 0 0 2px rgba(${style.hue}, 0.2)`,
          color: "#0A0A0F",
        }}
      >
        {level.n}
      </div>

      {/* Card */}
      <button
        onClick={onToggle}
        className="w-full text-left noam-card overflow-hidden transition-all group hover:border-white/15"
        style={{
          borderColor: expanded ? `rgba(${style.hue}, 0.45)` : undefined,
          background: expanded ? `linear-gradient(135deg, rgba(${style.hue}, 0.06) 0%, rgba(${style.hue}, 0.02) 100%)` : undefined,
        }}
        data-testid={`orig-fractale-toggle-${level.n}`}
      >
        <div className="p-4 sm:p-5 flex items-start gap-4">
          {/* Icon */}
          <div
            className="w-11 h-11 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center text-2xl shrink-0 transition-transform group-hover:scale-105"
            style={{
              background: `rgba(${style.hue}, 0.12)`,
              border: `1px solid rgba(${style.hue}, 0.4)`,
              color: style.color,
            }}
          >
            {style.icon}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: style.color }}>
                Niveau {level.n} · {style.label}
              </span>
              {level.doctrine_ref && (
                <span className="text-[9px] text-zinc-600 italic">{level.doctrine_ref}</span>
              )}
            </div>
            <div className="mt-1 flex items-baseline gap-2 flex-wrap">
              <h3 className="text-lg font-semibold text-white">{level.doctrine_title || level.title}</h3>
              <span className="text-xs text-zinc-400">— {level.subtitle}</span>
            </div>
            <p className="text-sm text-zinc-300 mt-2 leading-relaxed">
              {level.narrative || "Narrative doctrinale à définir."}
            </p>

            {/* Petit compteur */}
            {hasBuckets && (
              <div className="mt-3 flex items-center gap-4 text-[11px] text-zinc-400">
                <span className="inline-flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: style.color }} />
                  {totalOpps} opportunité{totalOpps > 1 ? "s" : ""} rattachée{totalOpps > 1 ? "s" : ""}
                </span>
                {totalTicket > 0 && (
                  <span className="text-zinc-500">
                    · {totalTicket.toFixed(1)} M€ brut
                    {factor != null && factor < 1 && (
                      <span className="ml-1" style={{ color: style.color }}>· {(totalTicket * factor).toFixed(1)} M€ net</span>
                    )}
                  </span>
                )}
              </div>
            )}
          </div>

          <div
            className="text-xs text-zinc-500 shrink-0 flex items-center gap-1 self-center transition-transform"
            style={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)" }}
          >
            <ChevronDown className="w-4 h-4" />
          </div>
        </div>

        {/* Expanded detail */}
        {expanded && (
          <div
            className="border-t px-4 sm:px-5 py-4"
            style={{ borderColor: `rgba(${style.hue}, 0.25)` }}
            data-testid={`orig-fractale-detail-${level.n}`}
          >
            {!hasBuckets ? (
              <div className="text-xs text-zinc-500 py-4 text-center">
                Ce niveau attend tes premières opportunités pour se peupler. En attendant, la strate doctrinale reste visible pour te guider.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                {buckets.map((b, i) => (
                  <div
                    key={`${level.n}-${i}-${b.label}`}
                    className="rounded-md p-3 border transition-colors hover:border-white/15"
                    style={{
                      background: `rgba(${style.hue}, 0.04)`,
                      borderColor: `rgba(${style.hue}, 0.2)`,
                    }}
                    data-testid={`orig-bucket-${level.n}-${i}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-xs font-semibold text-zinc-100 truncate" title={b.label}>
                        {b.label}
                      </div>
                      <div className="text-[10px] text-zinc-500 tabular-nums shrink-0 ml-2">
                        {b.count} opp
                      </div>
                    </div>
                    {b.ticket_total_meur != null && b.ticket_total_meur > 0 && (
                      <div className="text-[10px] text-zinc-500">
                        Ticket : {b.ticket_total_meur.toFixed(1)} M€
                        {factor != null && factor < 1 && (
                          <span className="ml-1.5" style={{ color: style.color }}>
                            · Net : {(b.ticket_total_meur * factor).toFixed(1)} M€
                          </span>
                        )}
                      </div>
                    )}
                    {(b.opportunites || []).slice(0, 3).map((o) => (
                      <div key={o.id} className="text-[11px] text-zinc-400 mt-1 truncate" title={o.titre}>
                        · {o.titre}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-3 text-[10px] text-zinc-500 flex-wrap">
                {level.context_counts && Object.entries(level.context_counts).map(([k, v]) => (
                  <span key={k} className="px-1.5 py-0.5 rounded" style={{ background: `rgba(${style.hue}, 0.08)`, color: style.color }}>
                    {k} : {v}
                  </span>
                ))}
              </div>
              <FrictionGapButton scope="fractale_level" level={level.n} />
            </div>
          </div>
        )}
      </button>
    </div>
  );
}


// ============================================================================
// Friction Gap button + Modal
// ============================================================================
export function FrictionGapButton({ scope, oppId, level, label }) {
  const { withActifs } = useActifs();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [stale, setStale] = useState(false); // true si on affiche un cache

  const run = async () => {
    // 1) Charger d'abord le dernier snapshot persisté (instant) pour ne JAMAIS partir d'écran vide
    setOpen(true);
    setLoading(true);
    setError("");
    setStale(false);
    try {
      const params = { scope };
      if (oppId) params.opportunite_id = oppId;
      if (level) params.fractale_level = level;
      const last = await api.get("/workspace/origination/friction-gap/last", { params });
      if (last.data?.found) {
        setData(last.data);
        setStale(true);
      } else {
        setData(null);
      }
    } catch { /* non-bloquant */ }

    // 2) Lancer le scan frais. Si succès, on remplace. Si 502/timeout, on garde le cache.
    try {
      const payload = { scope, with_actifs: withActifs };
      if (oppId) payload.opportunite_id = oppId;
      if (level) payload.fractale_level = level;
      const r = await api.post("/workspace/origination/friction-gap", payload);
      setData(r.data);
      setStale(false);
      setError("");
    } catch (e) {
      const msg = fmtError(e, "Erreur Friction Gap");
      setError(msg);
      // Ne PAS fermer le modal — on garde le cache affiché si présent.
      if (!data) {
        notify.error(msg);
      } else {
        notify.error("Timeout — dernière analyse conservée. Réessayez quand vous voulez.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={run}
        className="text-[10px] px-2 py-1 rounded border border-[#EF9F27]/40 bg-[#EF9F27]/10 text-[#EF9F27] hover:bg-[#EF9F27]/20 inline-flex items-center gap-1"
        data-testid={`orig-friction-gap-btn${oppId ? `-${oppId}` : level ? `-l${level}` : ""}`}
      >
        <AlertTriangle className="w-3 h-3" />
        {label || t("saas.origination.frictionGap", { defaultValue: "Friction Gap" })}
      </button>
      {open && (
        <FrictionGapModal
          data={data}
          loading={loading}
          error={error}
          stale={stale}
          oppId={oppId}
          scope={scope}
          level={level}
          onRetry={run}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function FrictionGapModal({ data, loading, error, stale, oppId, scope, level, onRetry, onClose }) {
  const [bgStarting, setBgStarting] = useState(false);
  const launchBackground = async () => {
    setBgStarting(true);
    try {
      const params = { scope };
      if (oppId) params.opportunite_id = oppId;
      if (level) params.fractale_level = level;
      await startLLMJob("friction_gap", params);
      notify.success("Analyse lancée en arrière-plan. Vous serez notifié(e) au résultat.");
      onClose();
    } catch (e) {
      notify.error(fmtError(e, "Impossible de lancer le job"));
    } finally {
      setBgStarting(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="orig-friction-modal">
      <div className="noam-card max-w-2xl w-full max-h-[90vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-[#EF9F27]" />
            <h3 className="text-base font-semibold text-zinc-100">Friction Gap</h3>
            {stale && !loading && (
              <span className="text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider bg-[#7F77DD]/15 text-[#7F77DD] border border-[#7F77DD]/30" title="Résultat conservé depuis le dernier scan réussi">
                Cache
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {!loading && (
              <button
                onClick={launchBackground}
                disabled={bgStarting}
                className="text-[10px] px-2 py-1 rounded border border-[#7F77DD]/40 bg-[#7F77DD]/10 text-[#7F77DD] hover:bg-[#7F77DD]/20 inline-flex items-center gap-1 disabled:opacity-50"
                data-testid="orig-friction-bg"
                title="Lance l'analyse en arrière-plan — vous pouvez fermer ce modal et continuer à travailler"
              >
                {bgStarting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                Arrière-plan
              </button>
            )}
            {!loading && data && (
              <button
                onClick={onRetry}
                className="text-[10px] px-2 py-1 rounded border border-[#EF9F27]/40 bg-[#EF9F27]/10 text-[#EF9F27] hover:bg-[#EF9F27]/20 inline-flex items-center gap-1"
                data-testid="orig-friction-retry"
                title="Relancer une analyse NOAM"
              >
                <Loader2 className="w-3 h-3" /> Re-scanner
              </button>
            )}
            <button onClick={onClose} className="p-1 hover:bg-white/10 rounded" data-testid="orig-friction-close">
              <XIcon className="w-4 h-4 text-zinc-400" />
            </button>
          </div>
        </div>

        {loading && (
          <div className="text-zinc-400 text-sm py-3 text-center border border-white/5 bg-white/[0.02] rounded mb-3">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Analyse NOAM en cours… (peut prendre 15-45s)
          </div>
        )}

        {error && !loading && (
          <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded p-3 mb-3" data-testid="orig-friction-error">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold">NOAM n'a pas répondu à temps</div>
                <div className="text-zinc-400 mt-1">{error}</div>
                {data && <div className="text-zinc-500 mt-1">→ La dernière analyse est affichée ci-dessous.</div>}
                <button
                  onClick={onRetry}
                  className="mt-2 text-[10px] px-2 py-1 rounded bg-red-500/20 border border-red-500/40 text-red-200 hover:bg-red-500/30 inline-flex items-center gap-1"
                  data-testid="orig-friction-error-retry"
                >
                  <Loader2 className="w-3 h-3" /> Réessayer
                </button>
              </div>
            </div>
          </div>
        )}

        {!loading && !data && !error && (
          <div className="text-zinc-500 text-sm py-8 text-center">Aucune analyse disponible.</div>
        )}

        {data && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-16 h-16 rounded-full border-4 border-[#EF9F27] flex items-center justify-center">
                <span className="text-2xl font-bold text-[#EF9F27]" data-testid="orig-friction-score">{data.score_friction || "?"}</span>
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Score de friction</div>
                <div className="text-xs text-zinc-300 mt-1">{data.synthese}</div>
                <div className="text-[10px] text-zinc-500 mt-1">
                  Mode : {data.with_actifs ? "Avec actifs" : "Sans actifs"}
                  {data.cost_inaction_total_meur_monthly > 0 && (
                    <span className="ml-2 text-red-300" data-testid="orig-friction-cost-total">
                      · Coût d'inaction : <strong>{Number(data.cost_inaction_total_meur_monthly).toFixed(2)} M€/mois</strong>
                      {data.cost_inaction_horizon_months > 0 && (
                        <span className="text-zinc-500"> · horizon {data.cost_inaction_horizon_months} mois</span>
                      )}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {data.frictions && data.frictions.length > 0 && (
              <div data-testid="orig-friction-list">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Frictions identifiées</div>
                <div className="space-y-2">
                  {data.frictions.map((f, i) => (
                    <div key={i} className="border border-white/10 rounded p-3 bg-white/[0.02]" data-testid={`orig-friction-${i}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-xs font-semibold text-zinc-100">{f.label}</div>
                        <div className="flex items-center gap-1.5">
                          {f.cost_inaction_meur_monthly > 0 && (
                            <span className="text-[9px] text-red-300 px-1.5 py-0.5 bg-red-500/10 border border-red-500/30 rounded" title="Coût d'inaction mensuel estimé">
                              {Number(f.cost_inaction_meur_monthly).toFixed(2)} M€/mois
                            </span>
                          )}
                          <span className="text-[9px] uppercase tracking-wider text-zinc-500 px-1.5 py-0.5 bg-white/5 rounded">{f.categorie}</span>
                        </div>
                      </div>
                      <div className="text-xs text-zinc-400 mt-1">{f.comment_lever}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.chemin_recommande && data.chemin_recommande.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Chemin recommandé</div>
                <ol className="space-y-1">
                  {data.chemin_recommande.map((s, i) => (
                    <li key={i} className="text-xs text-zinc-300 flex items-start gap-2">
                      <span className="text-[#7F77DD] font-bold">{i + 1}.</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <div className="flex items-center gap-2 pt-3 border-t border-white/5">
              <Link
                to={`/app/chainage${oppId ? `?prefill_opp=${oppId}` : `?prefill_level=${level || ""}`}`}
                className="text-[10px] px-3 py-1.5 rounded border border-[#378ADD]/40 bg-[#378ADD]/10 text-[#378ADD] inline-flex items-center gap-1"
                data-testid="orig-link-chainage"
              >
                <GitBranch className="w-3 h-3" /> Chaînage causal
              </Link>
              <Link
                to={`/app/utility-mapping${oppId ? `?prefill_opp=${oppId}` : `?prefill_level=${level || ""}`}`}
                className="text-[10px] px-3 py-1.5 rounded border border-[#1D9E75]/40 bg-[#1D9E75]/10 text-[#1D9E75] inline-flex items-center gap-1"
                data-testid="orig-link-utility"
              >
                <Network className="w-3 h-3" /> Cartographie des utilités
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Boutons d'action sur chaque opp / niveau (Friction Gap + Chaînage + Utility)
// ============================================================================
export function OppActionButtons({ oppId, level, compact = false }) {
  return (
    <div className={`flex items-center gap-1.5 flex-wrap ${compact ? "" : "mt-2 pt-2 border-t border-white/5"}`} data-testid="orig-opp-actions">
      <FrictionGapButton scope={oppId ? "opportunite" : "fractale_level"} oppId={oppId} level={level} />
      <Link
        to={`/app/chainage${oppId ? `?prefill_opp=${oppId}` : `?prefill_level=${level || ""}`}`}
        className="text-[10px] px-2 py-1 rounded border border-[#378ADD]/40 bg-[#378ADD]/10 text-[#378ADD] hover:bg-[#378ADD]/20 inline-flex items-center gap-1"
        data-testid={`orig-link-chainage-${oppId || level}`}
        title="Chaînage causal"
      >
        <GitBranch className="w-3 h-3" /> Chaînage
      </Link>
      <Link
        to={`/app/utility-mapping${oppId ? `?prefill_opp=${oppId}` : `?prefill_level=${level || ""}`}`}
        className="text-[10px] px-2 py-1 rounded border border-[#1D9E75]/40 bg-[#1D9E75]/10 text-[#1D9E75] hover:bg-[#1D9E75]/20 inline-flex items-center gap-1"
        data-testid={`orig-link-utility-${oppId || level}`}
        title="Cartographie des utilités"
      >
        <Network className="w-3 h-3" /> Utilités
      </Link>
      {oppId && <Carto360Button subjectType="opportunite" subjectId={oppId} label="360°" compact />}
    </div>
  );
}
